// import useAuth from 'hooks/useAuth';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import handleAction from 'utils/action-manager/actionManager';
import { APIAction } from 'utils/action-manager/api/apiManager';
import { convertToJSONCustom } from 'utils/json-custom';

export type DTDevelopmentItem = {
  id: string;
  type: 'development' | 'property';
  highlighted: boolean;
  name?: string;
  additional?: any;
};

type DTDevelopmentContextType = {
  dtDevelopment: DTDevelopmentItem[];
  dtDevelopmentId: string | null;
  dtDevelopmentName: string | null;
  dtId: string | null;
  dtCommercialId: string | null;
  isDTDevelopmentEnabled: boolean;
  isDrawerOpen: boolean;
  isEditing: boolean;
  dtDevelopmentUpdated: boolean;
  enableDTDevelopment: (id?: string, dtId?: string, email?: string) => void;
  startNewDTDevelopmentWithDTCommercialId: (id: string, dtId: string, email?: string) => void;
  toggleDTDevelopmentDrawer: () => void;
  addItem: (item: DTDevelopmentItem) => void;
  removeItem: (id: string) => void;
  toggleHighlight: (id: string) => void;
  isItemInDTDevelopment: (id: string, type: 'development' | 'property') => boolean;
  loadDTDevelopmentForEditing: (
    dtDevelopmentId: string,
    items: DTDevelopmentItem[],
    dtDevelopmentName?: string,
    dtCommercialId?: string,
    dtId?: string
  ) => void;
  clearDTDevelopment: (clearName?: boolean) => void;
  saveDTDevelopment: () => void;
  getDTDevelopmentItemCount: () => number;
  setDTDevelopmentName: (name: string | null) => void;
  setDTId: (id: string | null) => void;
  setDTCommercialId: (id: string | null) => void;
  setDTDevelopmentUpdated: (updated: boolean) => void;
};

// ==============================|| OFFERS - CONTEXT & PROVIDER ||============================== //

const DTDevelopmentContext = createContext<DTDevelopmentContextType | undefined>(undefined);

export const DTDevelopmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dtDevelopment, setDTDevelopment] = useState<DTDevelopmentItem[]>([]);
  const [isDTDevelopmentEnabled, setIsDTDevelopmentEnabled] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [dtDevelopmentUpdated, setDTDevelopmentUpdated] = useState<boolean>(false);
  const [dtDevelopmentId, setDTDevelopmentId] = useState<string | null>(null);
  const [dtDevelopmentName, setDTDevelopmentName] = useState<string | null>(null);
  const [dtId, setDTId] = useState<string | null>(null);
  const [dtCommercialId, setDTCommercialId] = useState<string | null>(null);
  const isInitialMount = useRef(true);

  // const { user } = useAuth();

  // Initial load by local storage (context between different tabs)
  //   useEffect(() => {
  //     const savedDTDevelopment = localStorage.getItem('dtDevelopment');
  //     const savedIsDTDevelopmentEnabled = localStorage.getItem('isDTDevelopmentEnabled');
  //     if (savedDTDevelopment) setDTDevelopment(JSON.parse(savedDTDevelopment));
  //     if (savedIsDTDevelopmentEnabled) setIsDTDevelopmentEnabled(JSON.parse(savedIsDTDevelopmentEnabled));
  //   }, []);
  useEffect(() => {
    const savedDTDevelopment = localStorage.getItem('dtDevelopment');
    const savedIsDTDevelopmentEnabled = localStorage.getItem('isDTDevelopmentEnabled');
    const savedDTDevelopmentName = localStorage.getItem('dtDevelopmentName');
    const savedDTId = localStorage.getItem('dtId');
    const savedDTCommercialId = localStorage.getItem('dtCommercialId');

    const dtDevelopmentData = savedDTDevelopment ? JSON.parse(savedDTDevelopment) : [];
    const dtDevelopmentEnabled = savedIsDTDevelopmentEnabled ? JSON.parse(savedIsDTDevelopmentEnabled) : false;

    setDTDevelopment(dtDevelopmentData);
    // setIsDTDevelopmentEnabled(dtDevelopmentData.length > 0 && dtDevelopmentEnabled);
    setDTDevelopmentName(savedDTDevelopmentName || null);
    setDTId(savedDTId || null);
    setDTCommercialId(savedDTCommercialId || null);

    if ((dtDevelopmentData.length > 0 || savedDTDevelopmentName || savedDTId || savedDTCommercialId) && dtDevelopmentEnabled) {
      setIsDTDevelopmentEnabled(true);
    } else {
      setIsDTDevelopmentEnabled(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save dtDevelopment to local storage (anytime it changes)
  //   useEffect(() => {
  //     localStorage.setItem('dtDevelopment', JSON.stringify(dtDevelopment));
  //     localStorage.setItem('isDTDevelopmentEnabled', JSON.stringify(isDTDevelopmentEnabled));
  //   }, [dtDevelopment, isDTDevelopmentEnabled]);
  useEffect(() => {
    if (!isInitialMount.current) {
      localStorage.setItem('dtDevelopment', JSON.stringify(dtDevelopment));
      localStorage.setItem('isDTDevelopmentEnabled', JSON.stringify(isDTDevelopmentEnabled));
      localStorage.setItem('dtDevelopmentName', dtDevelopmentName || '');
      localStorage.setItem('dtId', dtId || '');
      localStorage.setItem('dtCommercialId', dtCommercialId || '');
    } else {
      isInitialMount.current = false;
    }
  }, [dtDevelopment, isDTDevelopmentEnabled, dtDevelopmentName, dtId, dtCommercialId]);

  // Listener to synchronize changes between tabs
  //   useEffect(() => {
  //     const handleStorageChange = (event: StorageEvent) => {
  //       if (event.key === 'dtDevelopment' && event.newValue) {
  //         const newDTDevelopment = JSON.parse(event.newValue);
  //         setDTDevelopment(newDTDevelopment);

  //         if (newDTDevelopment.length === 0) {
  //           setIsDTDevelopmentEnabled(false);
  //         }
  //       }
  //       if (event.key === 'isDTDevelopmentEnabled' && event.newValue) {
  //         const dtDevelopmentEnabled = JSON.parse(event.newValue);

  //         if (dtDevelopment.length > 0 || dtDevelopmentEnabled === false) {
  //           setIsDTDevelopmentEnabled(dtDevelopmentEnabled);
  //         }

  //         if (!dtDevelopmentEnabled) {
  //           setIsDrawerOpen(false);
  //         }
  //       }
  //       if (event.key === 'dtDevelopmentName' && event.newValue !== null) {
  //         setDTDevelopmentName(event.newValue || null);
  //       }
  //     };

  //     window.addEventListener('storage', handleStorageChange);

  //     return () => {
  //       window.removeEventListener('storage', handleStorageChange);
  //     };
  //   }, [dtDevelopment]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'dtDevelopment' && event.newValue) {
        const newDTDevelopment = JSON.parse(event.newValue);
        setDTDevelopment(newDTDevelopment);
      }

      if (event.key === 'isDTDevelopmentEnabled' && event.newValue) {
        const dtDevelopmentEnabled = JSON.parse(event.newValue);
        if (dtDevelopment.length > 0 || dtDevelopmentName || dtDevelopmentEnabled === false) {
          setIsDTDevelopmentEnabled(dtDevelopmentEnabled);
        }

        if (!dtDevelopmentEnabled) {
          setIsDrawerOpen(false);
        }
      }

      if (event.key === 'dtDevelopmentName' && event.newValue !== null) {
        setDTDevelopmentName(event.newValue || null);
        if (event.newValue) {
          setIsDTDevelopmentEnabled(true);
        }
      }

      if (event.key === 'dtId' && event.newValue !== null) {
        setDTId(event.newValue || null);
        if (event.newValue) {
          setIsDTDevelopmentEnabled(true);
        }
      }

      if (event.key === 'dtCommercialId' && event.newValue !== null) {
        setDTCommercialId(event.newValue || null);
        if (event.newValue) {
          setIsDTDevelopmentEnabled(true);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dtDevelopment, dtDevelopmentName, dtCommercialId]);

  const enableDTDevelopment = (id?: string, dtId?: string) => {
    setIsDTDevelopmentEnabled(true);
    setIsEditing(false);
    setDTDevelopmentId(null);
    if (dtId) setDTId(dtId);
    if (id) setDTCommercialId(id);
  };

  const startNewDTDevelopmentWithDTCommercialId = (id: string, dtId: string) => {
    setDTId(dtId);
    setDTCommercialId(id);
    enableDTDevelopment();
    // setIsDrawerOpen(true); // opcional: abre el drawer automáticamente
  };

  const toggleDTDevelopmentDrawer = () => setIsDrawerOpen((prev) => !prev);

  const addItem = (item: DTDevelopmentItem) => {
    setDTDevelopment((prevDTDevelopment) => [...prevDTDevelopment, item]);
  };

  const removeItem = (id: string) => {
    setDTDevelopment((prevDTDevelopment) => prevDTDevelopment.filter((item) => item.id !== id));
  };

  const toggleHighlight = (id: string) => {
    setDTDevelopment((prevDTDevelopment) =>
      prevDTDevelopment.map((item) => (item.id === id ? { ...item, highlighted: !item.highlighted } : item))
    );
  };

  const isItemInDTDevelopment = (id: string, type: 'development' | 'property') => {
    return dtDevelopment.some((item) => item.id === id && item.type === type);
  };

  const loadDTDevelopmentForEditing = (
    dtDevelopmentId: string,
    items: DTDevelopmentItem[],
    name?: string,
    dtCommercialId?: string,
    dtId?: string
  ) => {
    setDTDevelopment(items);
    setIsDTDevelopmentEnabled(true);
    setIsEditing(true);
    setDTDevelopmentId(dtDevelopmentId);
    setDTDevelopmentName(name || null);
    setDTId(dtId || null);
    setDTCommercialId(dtCommercialId || null);
    toggleDTDevelopmentDrawer();
  };

  const clearDTDevelopment = (clearName: boolean = false) => {
    setDTDevelopment([]);
    setIsDTDevelopmentEnabled(false);
    setIsDrawerOpen(false);
    setIsEditing(false);
    setDTDevelopmentId(null);

    if (clearName) {
      setDTDevelopmentName(null);
      setDTId(null);
      setDTCommercialId(null);
    }
  };

  const saveDTDevelopment = () => {
    if (isEditing && dtDevelopmentId) {
      // Update dtDevelopment
      const data = {
        // status: 'active',
        // dtid: dtId,
        name: dtDevelopmentName,
        dtcommercialid: dtCommercialId,
        // [ TO-DO ] - Make dynamic switch between developments and properties (when properties are included)
        developments: dtDevelopment,
        // properties: dtDevelopment,
        updatedate: new Date().toISOString()
      };

      const apiAction: APIAction = {
        type: 'api',
        entity: 'dt-developments',
        method: 'update',
        data: convertToJSONCustom(data),
        id: dtDevelopmentId
      };

      handleAction(apiAction)
        .then((res) => {
          //   console.log(res);
          const newData = {
            dtid: dtId,
            dtcommercialid: dtCommercialId,
            entity: 'development',
            entityid: dtDevelopmentId,
            type: 'update',
            description: 'development-update-event',
            creationdate: new Date().toISOString(),
            updatedate: new Date().toISOString()
          };

          const apiAction: APIAction = {
            type: 'api',
            entity: 'dt-events',
            method: 'create',
            data: newData
          };

          handleAction(apiAction)
            .then((response: any) => {
              console.log(response);
            })
            .catch((error) => {
              console.error(error);
            });

          dispatch(
            openSnackbar({
              open: true,
              message: 'DT Developments updated successfully.',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              close: false,
              setTimeout: 10
            })
          );
          setDTDevelopmentUpdated(true);
          clearDTDevelopment(true);
        })
        .catch((err) => console.error(err));
    } else {
      // Create dtDevelopment
      const data = {
        status: 'active',
        dtid: dtId,
        name: dtDevelopmentName,
        dtcommercialid: dtCommercialId,
        // [ TO-DO ] - Make dynamic switch between developments and properties (when properties are included)
        developments: dtDevelopment,
        // properties: dtDevelopment,
        creationdate: new Date().toISOString(),
        updatedate: new Date().toISOString()
      };

      const apiAction: APIAction = {
        type: 'api',
        entity: 'dt-developments',
        method: 'create',
        data: convertToJSONCustom(data)
      };

      handleAction(apiAction)
        .then((res: any) => {
          //   console.log(res);

          const newData = {
            dtid: dtId,
            dtcommercialid: dtCommercialId,
            entity: 'development',
            entityid: res?.insertedId,
            type: 'add',
            description: 'development-add-event',
            creationdate: new Date().toISOString(),
            updatedate: new Date().toISOString()
          };

          const apiAction: APIAction = {
            type: 'api',
            entity: 'dt-events',
            method: 'create',
            data: newData
          };

          handleAction(apiAction)
            .then((response: any) => {
              console.log(response);
            })
            .catch((error) => {
              console.error(error);
            });

          dispatch(
            openSnackbar({
              open: true,
              message: 'DT Developments created successfully.',
              variant: 'alert',
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              alert: {
                color: 'success'
              },
              close: false,
              setTimeout: 10
            })
          );
          setDTDevelopmentUpdated(true);
          clearDTDevelopment(true);
        })
        .catch((err) => console.error(err));

      //   console.log(apiAction);
    }
  };

  const getDTDevelopmentItemCount = () => {
    return dtDevelopment.length;
  };

  return (
    <DTDevelopmentContext.Provider
      value={{
        dtDevelopment,
        dtDevelopmentName,
        dtDevelopmentId,
        dtId,
        dtCommercialId,
        isDTDevelopmentEnabled,
        isDrawerOpen,
        isEditing,
        dtDevelopmentUpdated,
        enableDTDevelopment,
        startNewDTDevelopmentWithDTCommercialId,
        toggleDTDevelopmentDrawer,
        addItem,
        removeItem,
        toggleHighlight,
        isItemInDTDevelopment,
        loadDTDevelopmentForEditing,
        clearDTDevelopment,
        saveDTDevelopment,
        getDTDevelopmentItemCount,
        setDTDevelopmentName,
        setDTId,
        setDTCommercialId,
        setDTDevelopmentUpdated
      }}
    >
      {children}
    </DTDevelopmentContext.Provider>
  );
};

export const useDTDevelopment = () => {
  const context = useContext(DTDevelopmentContext);
  if (!context) throw new Error('context must be use inside provider');
  return context;
};
